import React, { useState } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'
import { createPortal } from 'react-dom'
import CLOSE_ICON from '@public/assets/close_dialog.svg'
import DIALOG_BAR_IMAGE from '@public/assets/common/dialog_confirmational.svg'
import '@css/HeartBeatDialog.css'
import { PermanentDeleteDialog } from './PermanentDeleteDialog'

interface DeleteConfirmDialogProps {
  confirmText?: string
  count?: number
  description?: string
  isMobile?: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title?: string
}

export function DeleteConfirmDialog({
  isMobile = false,
  onClose,
  onConfirm,
  count = 0,
  title,
  description,
  confirmText = 'Confirm',
}: DeleteConfirmDialogProps) {
  const [deleting, setDeleting] = useState(false)

  // Activity deletion uses the same compact confirmation as time entries.
  // Keep explicit titles/descriptions for other existing confirmation flows.
  if (!title && !description) {
    return (
      <PermanentDeleteDialog
        count={count}
        entity="activity"
        loading={deleting}
        onClose={onClose}
        onConfirm={async () => {
          if (deleting) return
          setDeleting(true)
          try {
            await onConfirm()
            setDeleting(false)
            onClose()
          } catch (error) {
            console.error('Failed to delete activities:', error)
            setDeleting(false)
          }
        }}
      />
    )
  }

  const resolvedTitle = title || `Delete ${count} Selected items?`
  const resolvedDescription =
    description ||
    'This action cannot be undone. Are you sure you want to permanently delete the these items?'

  return createPortal(
    <Dialog
      onClose={onClose}
      className={`heartbeat-dialog ${
        isMobile ? 'mobile-heartbeat-dialog' : ''
      }`}
    >
      {/* close */}
      <button
        onClick={event => {
          event.stopPropagation()
          onClose()
        }}
        onMouseEnter={event => event.stopPropagation()}
        onMouseLeave={event => event.stopPropagation()}
        onMouseMove={event => event.stopPropagation()}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#999',
        }}
        aria-label="Close"
      >
        <img src={CLOSE_ICON} alt="icon" />
      </button>

      <div
        className="heartbeat-card"
        onClick={e => e.stopPropagation()}
        onMouseMove={e => e.stopPropagation()}
        onMouseEnter={e => e.stopPropagation()}
        onMouseLeave={e => e.stopPropagation()}
      >
        {/* top image */}
        <div className="heartbeat-image">
          <img src={DIALOG_BAR_IMAGE} alt="" />
        </div>

        {/* content */}
        <div className="heartbeat-content">
          <h2>{resolvedTitle}</h2>

          <p
            className="heartbeat-description"
            style={{
              marginBottom: 20,
              fontSize: 14,
            }}
          >
            {resolvedDescription}
          </p>
        </div>

        {/* footer */}
        <div
          className="heartbeat-footer"
          style={{
            display: 'flex',
            gap: 8,
          }}
        >
          <button
            className="cancel-button"
            onClick={onClose}
            style={isMobile ? { flex: '1 1 0', minWidth: 0 } : undefined}
          >
            Cancel
          </button>

          <button
            className="confirm-button primary-button"
            onClick={onConfirm}
            style={isMobile ? { flex: '1 1 0', minWidth: 0 } : undefined}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Dialog>,
    document.body
  )
}
